import { useState } from "react";
import {
  Combobox,
  FormModal,
  Input,
  Textarea,
} from "@marekl/adminer/components";
import { notifySuccess, notifyWarning } from "@marekl/adminer/api";
import { useSenderList, type Sender } from "../../lib/queries/senders";
import { useTemplateTestSend } from "../../lib/queries/templates";
import styles from "./TemplateDetailPage.module.css";

interface TestSendModalProps {
  templateId: string;
  /** Template channel — the sender list is narrowed to it. */
  channel: string;
  /** Current sample-data JSON, prefilled as the data override. */
  sampleData: string;
  onClose: () => void;
}

/**
 * Sends a test message from the current draft (falls back to the published
 * version server-side when no draft exists). Data defaults to the sample
 * data; the override textarea lets the user tweak it per send.
 */
export function TestSendModal({
  templateId,
  channel,
  sampleData,
  onClose,
}: TestSendModalProps) {
  const senders = useSenderList();
  const testSend = useTemplateTestSend(templateId);
  const [sender, setSender] = useState<string | null>(null);
  const [to, setTo] = useState("");
  const [data, setData] = useState(sampleData);
  const [dataError, setDataError] = useState<string | null>(null);

  const options = (senders.data ?? []).filter(
    (s) => s.active && s.channel === channel,
  );

  async function send() {
    let parsed: unknown;
    const trimmed = data.trim();
    if (trimmed !== "") {
      try {
        parsed = JSON.parse(trimmed);
        setDataError(null);
      } catch {
        setDataError("Invalid JSON.");
        return;
      }
    }
    try {
      const res = await testSend.mutateAsync({
        sender: sender!,
        to: to.trim(),
        data: parsed,
      });
      notifySuccess(`The test message was queued (ID ${res.id}).`);
      // The send succeeded — a deliverability caveat (e.g. body size near
      // Gmail's clip limit) is a warning, not part of the success line.
      if (res.warning) notifyWarning(res.warning);
      onClose();
    } catch {
      // Shown by the FormModal error slot (the mutation suppresses the toast).
    }
  }

  const isSMS = channel === "sms";

  return (
    <FormModal
      open
      title={isSMS ? "Test message" : "Test email"}
      onClose={onClose}
      busy={testSend.isPending}
      canSubmit={!!sender && !!to.trim()}
      error={testSend.error ?? undefined}
      submitLabel="Send test"
      onSubmit={() => void send()}
    >
      <Combobox<Sender>
        label="Sender"
        options={options}
        value={sender}
        onChange={setSender}
        getOptionLabel={(s) =>
          // An SMS sender's from_address is a phone number when it is pinned
          // to a SIM and empty when it is not; rendering it in angle brackets
          // like a mailbox gives "Phone A <>".
          isSMS
            ? s.from_address
              ? `${s.name} (${s.from_address})`
              : s.name
            : `${s.name} <${s.from_address}>`
        }
        getOptionValue={(s) => s.key}
        getOptionKey={(s) => s.id}
        placeholder="Select a sender…"
        loading={senders.isLoading}
        required
      />
      <Input
        label="Recipient"
        type={isSMS ? "tel" : "email"}
        required
        value={to}
        onChange={(e) => setTo(e.target.value)}
        placeholder={isSMS ? "+420777123456" : "jane@example.com"}
        hint={isSMS ? "In international format, starting with +." : undefined}
        autoComplete="off"
      />
      <Textarea
        label="Data (JSON, optional)"
        rows={8}
        spellCheck={false}
        className={styles.jsonTextarea}
        value={data}
        onChange={(e) => setData(e.target.value)}
        error={dataError ?? undefined}
        hint="Prefilled with the template’s sample data; leaving it empty uses the sample data."
      />
    </FormModal>
  );
}
